/**
 * parentTodoAdapter — les quatre sources legacy de l'accueil → `UIParentTodo[]`.
 *
 * API-aware (`upcoming_activation`, `self_serve_reprise`, `attendance_flags_to_be_recovered`,
 * `is_payment_blocked`). Jamais importé par le DS.
 *
 * Port littéral des trois prédicats de bandeau et de leur exclusivité, qui vivent dans
 * `src/common/**` — interdit à `ports/**` (`src/ports/CLAUDE.md` § Dependency rules). Les
 * symboles recopiés, avec leur fichier :
 *
 * - `canActivateSuite` — `common/components/family/activation/landingState.js`
 * - `canShowRepriseBanner` — `common/utils/selfServeRepriseDisplay.js`
 * - `isSummerBannerVisible` — `common/utils/summer2026.js` ; ses bornes et ses statuts entérinés
 *   sont lus dans `adapters/parent/Subscription/summerGates.ts`, jamais recopiés ici
 * - l'accroche, le mot de saison et l'échéance de remise — `common/utils/activationSuiteDisplay.js`,
 *   et l'accroche de reprise `getRepriseHero` — `common/utils/selfServeRepriseDisplay.js` : les
 *   deux sont lues dans `adapters/parent/shared/activationDisplay.ts`, jamais recopiées ici
 * - la chaîne « une seule bannière à la fois » (été > activation > reprise) et le compteur de
 *   rattrapages — `common/components/family/ParentSubscribeScreen.js` et
 *   `student/tutoring/StudentLiveSessionsList.js`
 *
 * L'accueil legacy est retiré ; les symboles que d'autres écrans legacy lisent encore
 * (`summer2026.js`, `activationSuiteDisplay.js`, `StudentLiveSessionsList.js`) restent une seconde
 * copie qui peut diverger : une évolution d'une règle commune se reporte ici.
 */
import {
    activationAdvantageLabel,
    getActivationHeadline,
    getDiscountUrgency,
    getRepriseHero,
} from "../shared/activationDisplay"
import { isBlockOn, TENANT_BLOCKS } from "../../../../../data/utility/tenantBlocks"
import {
    daysLeftLabel,
    recoveryDeadline,
    type ApiRecoverableAbsence,
    type RecoveryDeadline,
} from "../shared/recoveryDeadline"
import { type ApiSummerConfig, isConfirmedSummerStatus, isSummer2026Visible } from "../Subscription/summerGates"
import { toUITutorSlotMoves, type ApiTutorSlotMove } from "../TutorSlotMoves/tutorSlotMoveAdapter"
import { formatShortDayTime } from "../../../ui/formatters/dateFormatter"
import { type UIParentTodo } from "../../../ui/types"

/** `upcoming_activation` — `UserSubscriptionResource:51-52`. */
export type ApiUpcomingActivation = {
    type?: string | null
    /** Stage tenu pendant l'année : la suite reprend le lundi qui le suit (`SubscriptionDueDateEvaluator`). */
    held_in_year?: boolean | null
    eligible?: boolean | null
    recommended_offer?: unknown
    planned?: unknown
    discount_expires_at?: string | null
}

/** `self_serve_reprise` — `UserSubscriptionResource:58`. */
export type ApiSelfServeReprise = {
    eligible?: boolean | null
    season?: string | null
    url?: string | null
}

/** Les champs que les gardes lisent sur `formData.user` / `currentUser.object`. */
export type ApiTodoUser = {
    id?: string | number
    firstname?: string
    is_payment_blocked?: boolean
    payment_url?: string | null
    upcoming_activation?: ApiUpcomingActivation | null
    self_serve_reprise?: ApiSelfServeReprise | null
    summer_summer_status?: string | null
    has_summer_fast_track?: boolean
    subscription_state?: { is_manual_sub?: boolean; status?: string } | null
}

export type ParentTodoSources = {
    /** `formData.user` — `UserSubscriptionResource`. */
    student?: ApiTodoUser | null
    /** `currentUser.object` — `UserResource` ; c'est lui qui porte l'été et `payment_url`. */
    currentUser?: ApiTodoUser | null
    summerConfig?: ApiSummerConfig | null
    attendanceFlagsToBeRecovered?: ApiRecoverableAbsence[] | null
    /** `GET api/v1/tutor-slot-moves` — les choix « garder son tuteur » en attente. */
    tutorSlotMoves?: ApiTutorSlotMove[] | null
    /** Le prénom affiché dans les copies — l'enfant sélectionné, puis `formData.user`. */
    childFirstname?: string | null
}

// ── Prédicats recopiés ─────────────────────────────────────────────────────────────────────

/** Port littéral de `canActivateSuite` (`landingState.js`). `recommended_offer` est portant. */
function canActivateSuite(activation: ApiUpcomingActivation | null | undefined): boolean {
    return !!activation && activation.type !== "none" && !!activation.eligible && !!activation.recommended_offer
}

/** Port littéral de `canShowRepriseBanner` — une bannière sans destination est un cul-de-sac. */
function canShowRepriseBanner(block: ApiSelfServeReprise | null | undefined): boolean {
    return !!block?.eligible && !!block?.url
}

/** Port littéral de `isSummerBannerVisible` (`summer2026.js`). */
function isSummerBannerVisible(user: ApiTodoUser | null | undefined, summerConfig: ApiSummerConfig | null | undefined) {
    const status = user?.subscription_state?.status
    const isManualSub = !!user?.subscription_state?.is_manual_sub
    const isOnGenericTrial = status === "on_generic_trial"
    const isChurnedSubscription = ["ended", "expired_trial", "-"].includes(status ?? "")
    return (
        isSummer2026Visible(undefined, summerConfig) &&
        !isManualSub &&
        !isOnGenericTrial &&
        !isChurnedSubscription &&
        !user?.has_summer_fast_track &&
        !isConfirmedSummerStatus(user?.summer_summer_status)
    )
}

/**
 * Le temps qui reste pour l'absence rattrapable qui expire la PREMIÈRE — « Encore 9 jours pour
 * la rattraper », ou, quand il y en a plusieurs, « 2 absences, encore 3 jours pour la première ».
 * Le compte est celui de l'étape « Quelle absence ? » (`recoveryDeadline`, `daysLeftLabel`) : sans
 * lui ici, le parent d'une seule absence ne voyait jamais l'échéance, parce que cette étape est sautée.
 */
function recoveryDetail(count: number, daysLeft: number): string {
    const time = daysLeftLabel(daysLeft, "sentence")
    if (count === 1) return `${time.charAt(0).toUpperCase()}${time.slice(1)} pour la rattraper`
    return `${count} absences, ${time} pour la première`
}

// ── L'assemblage ───────────────────────────────────────────────────────────────────────────

/**
 * Les tâches de l'accueil, dans l'ordre du bloc « À faire » : un changement de tuteur par choix
 * en attente (sans réponse, il se décide seul), puis la bannière (activation OU reprise, jamais
 * les deux), puis le rattrapage, puis le paiement.
 *
 * `isParentMode` n'est pas un argument : l'accueil parent est toujours en mode parent
 * (`RootNavigator`), comme le dit le legacy à la garde qu'il garde « par symétrie ».
 */
export function toUIParentTodos(sources: ParentTodoSources): UIParentTodo[] {
    const { student, currentUser, summerConfig, attendanceFlagsToBeRecovered, tutorSlotMoves, childFirstname } = sources

    // Double lecture du legacy : le bloc est projeté sur les deux tranches, l'une ou l'autre
    // peut avoir hydraté en premier.
    const activation = student?.upcoming_activation ?? currentUser?.upcoming_activation ?? null
    const reprise = student?.self_serve_reprise ?? currentUser?.self_serve_reprise ?? null
    const plannedActivation = activation?.planned ?? null
    const firstname = childFirstname || student?.firstname || "votre enfant"

    // Le bandeau été n'est lu que pour l'enfant connecté, et il fusionne les deux tranches
    // exactement comme `ParentSubscribeScreen` (`summer_summer_status` vit sur `currentUser`).
    const identityMatches =
        student?.id != null && currentUser?.id != null && String(student.id) === String(currentUser.id)
    const summerBannerVisible =
        identityMatches &&
        isSummerBannerVisible(
            {
                ...(currentUser ?? {}),
                ...(student ?? {}),
                summer_summer_status: currentUser?.summer_summer_status ?? student?.summer_summer_status,
            },
            summerConfig
        )
    const summerConfirmedBackend = isConfirmedSummerStatus(
        currentUser?.summer_summer_status ?? student?.summer_summer_status
    )
    const summerWindowOpen = isSummer2026Visible(undefined, summerConfig)

    const activationVisible =
        !summerBannerVisible &&
        !(summerWindowOpen && summerConfirmedBackend) &&
        !plannedActivation &&
        canActivateSuite(activation) &&
        !student?.subscription_state?.is_manual_sub

    // ⚠️ `!activationVisible` est PORTANT : les deux populations se recouvrent (une famille
    // partie peut rester activable). Sans lui, deux offres différentes au même instant.
    const repriseVisible =
        !summerBannerVisible && !activationVisible && !plannedActivation && canShowRepriseBanner(reprise)

    const todos: UIParentTodo[] = []

    // Pas d'équivalent legacy : le parcours n'existe que dans l'espace parent du design system.
    // Une ligne par choix — un choix par matière, qui peut porter plusieurs heures.
    for (const move of toUITutorSlotMoves(tutorSlotMoves, student?.id)) {
        todos.push({
            id: `tutor_change:${move.id}`,
            kind: "tutor_change",
            label: "Action requise : un de vos tuteurs n'est plus disponible",
            detail: `Avant le ${formatShortDayTime(move.deadline)}`,
            cta: "Voir",
            // Sans réponse, le nouveau tuteur garde le créneau : la ligne presse dès qu'elle paraît.
            tone: "danger",
            moveId: move.id,
        })
    }

    // La tâche mène au portail Abonnement : bloc « abonnement » éteint, pas de portail, pas de
    // tâche. La garde reste HORS de `activationVisible`, qui doit continuer d'éteindre la reprise.
    if (activationVisible && isBlockOn(currentUser, TENANT_BLOCKS.SUBSCRIPTION)) {
        const { deadlineLabel } = getDiscountUrgency(activation?.discount_expires_at)
        const heldInYear = !!activation?.held_in_year
        todos.push({
            id: "activation",
            kind: "activation",
            eyebrow: "2 minutes suffisent",
            label: getActivationHeadline(activation?.type, firstname, heldInYear),
            detail: deadlineLabel
                ? `Confirmez avant le ${deadlineLabel} pour profiter de ${activationAdvantageLabel(activation?.type, heldInYear)}`
                : "Sa place et ses horaires sont prêts à être confirmés.",
            cta: "Activer la suite",
            route: "parent-subscription",
            routeParams: { focus: "activation" },
        })
    }

    if (repriseVisible) {
        const { headline, subline } = getRepriseHero(reprise?.season, firstname)
        todos.push({
            id: "reprise",
            kind: "reprise",
            eyebrow: reprise?.season === "summer" ? "Cours d'été" : "Reprise",
            label: headline,
            detail: subline,
            cta: "Confirmer",
            externalUrl: reprise?.url ?? undefined,
        })
    }

    const flagsToRecover = attendanceFlagsToBeRecovered ?? []
    if (flagsToRecover.length > 0) {
        const recoverable = flagsToRecover.filter((flag) => !!flag?.is_recoverable_now)
        const soonest = recoverable
            .map((flag) => recoveryDeadline(flag))
            .reduce<RecoveryDeadline | null>(
                (min, next) => (min == null || next.daysLeft < min.daysLeft ? next : min),
                null
            )
        todos.push({
            id: "makeup",
            kind: "makeup",
            label: "Planifier un rattrapage",
            detail: soonest != null ? recoveryDetail(recoverable.length, soonest.daysLeft) : undefined,
            cta: "Planifier",
            count: recoverable.length,
            // Le seuil du compte à rebours du parcours fait passer la ligne au ton d'urgence.
            tone: soonest?.urgent ? "warning" : undefined,
            // Aucune route : l'accueil ouvre « Planifier un rattrapage » en feuille, sur place
            // (`useRecoverAbsenceFlow`), là où le legacy poussait l'écran `recover-absence`.
        })
    }

    // `is_payment_blocked` est la relance de recouvrement au niveau ÉLÈVE, avec son lien Stripe
    // — pas `subscription_state.status === "past_due"`, que le legacy n'affiche pas non plus.
    const paymentBlocked = student?.is_payment_blocked ?? currentUser?.is_payment_blocked
    if (paymentBlocked) {
        todos.push({
            id: "payment",
            kind: "payment",
            label: "Paiement en attente",
            detail: "Les séances sont suspendues tant qu'il n'est pas réglé.",
            cta: "Régulariser",
            // La seule tâche qui BLOQUE : tant qu'elle est là, aucune séance ne se rejoint. C'est
            // ce qui lui vaut un ton, toujours ; le rattrapage ne le prend qu'à l'approche de son
            // échéance, l'activation et la reprise jamais.
            tone: "warning",
            externalUrl: currentUser?.payment_url ?? undefined,
        })
    }

    return todos
}
